import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #bvSelectionSequence = 0;
  #bvSetState(state) {
    if (this.dataset.bvMediaGuard !== 'true') return;
    this.dataset.bvState = state;
    this.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    const status = this.querySelector('[data-bv-media-status]');
    if (status) {
      status.textContent = state === 'loading' ? this.dataset.bvLoadingMessage : this.dataset.bvErrorMessage;
      status.hidden = false;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const sequence = ++this.#bvSelectionSequence;
    this.#bvSetState('loading');
    event.promise
      .then(({ detail }) => {
        if (sequence !== this.#bvSelectionSequence || !this.isConnected) return;
        if (!detail?.html) throw new Error("Missing gallery response");

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) throw new Error("Missing gallery wrapper");

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (sequence !== this.#bvSelectionSequence || !this.isConnected) return;
        if (error?.name !== 'AbortError') this.#bvSetState('error');
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    // Zoom close transitions must return to the visible desktop slide, not the hidden grid.
    if (this.dataset.bvPcCarousel === 'true' && !this.closest('dialog') && window.matchMedia('(min-width: 750px)').matches) {
      return 'carousel';
    }
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
